/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import tw from 'twin.macro';

import React from 'react';
import { Tooltip } from '@rmwc/tooltip';
import { ReactComponent as QuestionSvg } from '../../assets/Icons/Vector_question.svg';
import { Notification } from '../UiControls/notification/Notification';
import { isEqual } from 'lodash';

interface IProps {
  type:
    | 'notification'
    | 'left'
    | 'warning'
    | 'information'
    | 'popup-manage-user'
    | 'error'
    | any
    | null;
  text: string | ReactElement;
  align:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight';
  action: 'hover' | 'click';
  customStyle?: any;
  notTransparent?: boolean;
  hideCloseButton?: boolean;
  stopPropagation?: boolean;
  exclusive?: boolean;
}

type HintCloseHandler = () => void;

let activeHintClose: HintCloseHandler | null = null;

const registerOpenHint = (close: HintCloseHandler) => {
  if (activeHintClose && activeHintClose !== close) {
    activeHintClose();
  }
  activeHintClose = close;
};

const unregisterOpenHint = (close: HintCloseHandler) => {
  if (activeHintClose === close) {
    activeHintClose = null;
  }
};

const HOVER_CLOSE_DELAY_MS = 100;

export const QuestionHint = React.memo((props: IProps) => {
  const { stopPropagation = true, exclusive = true } = props;

  // state
  const [isShowHint, setIsShowHint] = useState(false);

  // refs
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipContentRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // variables
  const isHoverMode = isEqual(props.action, 'hover');

  // functions
  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closeHint = useCallback(() => {
    cancelScheduledClose();
    unregisterOpenHint(closeHint);
    setIsShowHint(false);
  }, [cancelScheduledClose]);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      closeHint();
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelScheduledClose, closeHint]);

  const handleIconClick = (e: React.MouseEvent) => {
    if (isEqual(props.action, 'click')) {
      stopEventPropagation(e);
      if (isShowHint) {
        closeHint();
        return;
      }
      openHint();
    }
  };

  const stopEventPropagation = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const handleTooltipMouseLeave = () => {
    if (!isHoverMode) return;
    scheduleClose();
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!isHoverMode) return;
    stopEventPropagation(e);
    cancelScheduledClose();
    openHint();
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (!isHoverMode) return;
    stopEventPropagation(e);
    scheduleClose();
  };

  const handleTooltipMouseEnter = () => {
    if (!isHoverMode) return;
    cancelScheduledClose();
  };

  const openHint = useCallback(() => {
    if (exclusive) {
      registerOpenHint(closeHint);
    }
    setIsShowHint(true);
  }, [closeHint, exclusive]);

  const dismissOnScroll = useCallback(() => {
    flushSync(() => {
      setIsShowHint((prev) => {
        if (!prev) return prev;
        unregisterOpenHint(closeHint);
        return false;
      });
    });
  }, [closeHint]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        tooltipContentRef.current?.contains(target)
      ) {
        return;
      }
      closeHint();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [closeHint]);

  useEffect(() => {
    return () => {
      cancelScheduledClose();
      unregisterOpenHint(closeHint);
    };
  }, [cancelScheduledClose, closeHint]);

  useLayoutEffect(() => {
    if (!isShowHint) return;

    document.addEventListener('wheel', dismissOnScroll, {
      capture: true,
      passive: true,
    });
    document.addEventListener('scroll', dismissOnScroll, {
      capture: true,
      passive: true,
    });
    document.addEventListener('touchmove', dismissOnScroll, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener('wheel', dismissOnScroll, true);
      document.removeEventListener('scroll', dismissOnScroll, true);
      document.removeEventListener('touchmove', dismissOnScroll, true);
    };
  }, [isShowHint, dismissOnScroll]);

  return (
    <div
      ref={containerRef}
      onClick={stopEventPropagation}
      onMouseDown={stopEventPropagation}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip
        className="tooltip-manage-user"
        align={props.align}
        open={isShowHint}
        css={[
          props?.customStyle,
          overriddenCss({
            notTransparent: props?.notTransparent,
          }),
        ]}
        content={
          isShowHint ? (
            <div
              ref={tooltipContentRef}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
              tw="top-0 left[-1.5rem] font-size[1.6rem] line-height[1.9rem] border-radius[3px] absolute"
              className="custom-tooltip-text-container"
            >
              <Notification
                close={closeHint}
                type={props.type}
                message={props.text}
                hideCloseButton={props?.hideCloseButton}
                direction={null}
              />
            </div>
          ) : null
        }
      >
        <div
          tw="flex justify-center"
          className="questionsvg"
          onClick={handleIconClick}
        >
          <QuestionSvg />
        </div>
      </Tooltip>
    </div>
  );
});

const overriddenCss = ({ notTransparent }: { notTransparent?: boolean }) => css`
  &.rmwc-tooltip {
    opacity: ${notTransparent ? 1 : 0.9} !important;
  }

  &.rmwc-tooltip-zoom-leave,
  &.rmwc-tooltip-zoom-leave-active {
    animation-duration: 0s !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  a {
    ${tw`text-blue-600 hover:(underline)`}
  }
`;
